import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  LinearProgress,
  Tooltip,
  Alert,
  Avatar,
  Stack,
  Checkbox,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  School as SchoolIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  RestartAlt as RestartAltIcon,
  MailOutline as MailOutlineIcon,
  OutgoingMail as OutgoingMailIcon,
  BarChart as BarChartIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { adminFetch } from '../utils/adminFetch';
import { getAdminCourseName } from '../utils/courseDisplay';
import { useAuth } from '../contexts/AuthContext';
import {
  buildStudentsCsv,
  buildStudentsCsvFilename,
  downloadCsvFile,
  type CurriculumChapter,
  type CurriculumWorkshop,
} from '../utils/studentExportCsv';

interface Student {
  enrollmentId: string;
  group?: {
    id: string;
    name: string;
  } | null;
  student: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    isActive: boolean;
    defaultLanguage?: string | null;
    lastMessageAt?: string | null;
  };
  enrollment: {
    enrollmentDate: string;
    completionDate?: string;
    isActive: boolean;
    progress: number;
  };
  progress: {
    completedChapters: number;
    totalChapters: number;
    progressPercentage: number;
    totalTimeSpent: number;
    lastActivity?: string | null;
  };
  assessment?: {
    initialDone: boolean;
    finalDone: boolean;
  };
  chaptersProgress: Array<{
    chapterId: string;
    chapterTitle: string;
    chapterOrder: number;
    isCompleted: boolean;
    completionDate?: string;
    timeSpent: number;
    completedItems: number;
    totalItems?: number;
    completionPercentage?: number;
    rating?: number | null;
  }>;
  workshopsProgress?: Array<{
    workshopItemId: string;
    chapterId: string;
    status?: 'rated' | 'absent' | null;
    rating?: number | null;
  }>;
}

interface AvailableStudent {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface CourseStudentsData {
  course: {
    id: string;
    courseProject?: string;
    title: { [key: string]: string };
    defaultLanguage?: string;
    availableLanguages?: string[];
    totalChapters: number;
    diagnosticImpactEnabled?: boolean;
  };
  curriculum?: {
    chapters: CurriculumChapter[];
    workshops: CurriculumWorkshop[];
  };
  students: Student[];
  summary: {
    totalStudents: number;
    activeStudents: number;
    completedStudents: number;
    averageProgress: number;
  };
  scorm?: {
    selectedExportId: string | null;
    exports: Array<{
      id: string;
      exportVersion: string;
      clientId: string;
      active: boolean;
      tokenExpiresAt?: string | null;
      uniqueLearnersCount: number;
    }>;
  };
}

interface GroupInfo {
  id: string;
  name: string;
  studentCount: number;
}

const SCORM_NO_EMAIL_DOMAIN = '@no-email.scorm.local';
const isScormNoEmail = (email?: string): boolean =>
  Boolean(email && email.endsWith(SCORM_NO_EMAIL_DOMAIN));

const CourseStudents: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canManage = isAdmin;
  const isEntreprise = user?.role === 'entreprise';
  const isTrackingOnly = user?.role === 'coach' || isEntreprise;
  const canOpenStudentTracking = !isEntreprise;
  
  const [data, setData] = useState<CourseStudentsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [addStudentOpen, setAddStudentOpen] = useState(false);
  const [availableStudents, setAvailableStudents] = useState<AvailableStudent[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<AvailableStudent | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string>(''); // '' = tous les groupes
  const [groups, setGroups] = useState<GroupInfo[]>([]);
  const [addStudentGroupId, setAddStudentGroupId] = useState<string>('');
  const [selectedScormExportId, setSelectedScormExportId] = useState<string>('');
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);

  // Filtres : uniquement les groupes avec au moins 1 apprenant (périmètre courant)
  const filterGroups = groups.filter((g) => g.studentCount > 0);

  useEffect(() => {
    if (selectedGroupId && !filterGroups.some((g) => g.id === selectedGroupId)) {
      setSelectedGroupId('');
    }
  }, [selectedGroupId, filterGroups]);

  useEffect(() => {
    if (courseId) {
      fetchCourseStudents();
      fetchCourseGroups();
    }
  }, [courseId]);

  // Quand on ouvre la popup d'ajout et qu'on a déjà la liste des groupes,
  // pré-sélectionner le premier groupe si rien n'est encore choisi.
  useEffect(() => {
    if (addStudentOpen && groups.length > 0 && !addStudentGroupId) {
      setAddStudentGroupId(groups[0].id);
    }
  }, [addStudentOpen, groups, addStudentGroupId]);

  const fetchCourseStudents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams();
      if (selectedScormExportId) {
        params.set('scormExportId', selectedScormExportId);
      }
      const endpoint = `${process.env.REACT_APP_API_URL}/courses/${courseId}/students${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await adminFetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setData(data);
      } else {
        setError('Impossible de charger les apprenants de la formation');
      }
    } catch (error) {
      setError('Erreur lors du chargement des apprenants de la formation');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseGroups = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await adminFetch(`${process.env.REACT_APP_API_URL}/groups/course/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const groupsData = await response.json();
        const mapped = (groupsData || []).map((g: any) => ({
          id: g.id,
          name: g.name,
          studentCount: g.studentCount || 0
        }));
        setGroups(mapped);
        // Pré-sélectionner le premier groupe pour l'ajout d'apprenant
        if (mapped.length > 0) {
          setAddStudentGroupId(mapped[0].id);
        } else {
          setAddStudentGroupId('');
        }
      } else {
        setGroups([]);
        setAddStudentGroupId('');
      }
    } catch (err) {
      console.error('Erreur lors du chargement des groupes:', err);
      setGroups([]);
    }
  };

  const fetchAvailableStudents = async (search: string = '') => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await adminFetch(
        `${process.env.REACT_APP_API_URL}/courses/${courseId}/students/available?search=${encodeURIComponent(search)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setAvailableStudents(data.students);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des apprenants disponibles:', error);
    }
  };

  const handleAddStudent = async () => {
    if (!selectedStudent || !addStudentGroupId) return;

    try {
      setActionLoading('add');
      const token = localStorage.getItem('admin_token');
      const response = await adminFetch(`${process.env.REACT_APP_API_URL}/courses/${courseId}/students`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          studentEmail: selectedStudent.email,
          groupId: addStudentGroupId
        })
      });

      if (response.ok) {
        setAddStudentOpen(false);
        setSelectedStudent(null);
        setAddStudentGroupId('');
        fetchCourseStudents();
        fetchCourseGroups();
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Impossible d'ajouter l'apprenant");
      }
    } catch (error) {
      setError("Erreur lors de l'ajout de l'apprenant");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveStudent = async (enrollmentId: string) => {
    if (!window.confirm('Voulez-vous vraiment retirer cet apprenant de la formation ?')) {
      return;
    }

    try {
      setActionLoading(enrollmentId);
      const token = localStorage.getItem('admin_token');
      const response = await adminFetch(
        `${process.env.REACT_APP_API_URL}/courses/${courseId}/students/${enrollmentId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        fetchCourseStudents();
        fetchCourseGroups();
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Impossible de retirer l'apprenant de la formation");
      }
    } catch (error) {
      setError("Erreur lors du retrait de l'apprenant");
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetProgress = async (enrollmentId: string) => {
    if (
      !window.confirm(
        'Réinitialiser toute la progression de cet apprenant dans cette formation ? Les complétions de sections, temps passé, questionnaires et diagnostics seront effacés. Cette action est irréversible.'
      )
    ) {
      return;
    }

    try {
      setActionLoading(`reset:${enrollmentId}`);
      const token = localStorage.getItem('admin_token');
      const response = await adminFetch(
        `${process.env.REACT_APP_API_URL}/courses/${courseId}/students/${enrollmentId}/reset-progress`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        fetchCourseStudents();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Impossible de réinitialiser la progression');
      }
    } catch (error) {
      setError('Erreur lors de la réinitialisation de la progression');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatTime = (seconds: number) => {
    if (!seconds || seconds <= 0) return '0s';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 1) return `${seconds}s`;
    if (minutes < 60) return `${minutes}min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}min`;
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 100) return 'success';
    if (percentage >= 50) return 'primary';
    return 'warning';
  };

  const filteredStudents =
    selectedGroupId && data
      ? data.students.filter((s) => s.group && s.group.id === selectedGroupId)
      : data?.students || [];

  const scormTotalUniqueLearners = data?.scorm
    ? data.scorm.exports.reduce((sum, item) => sum + item.uniqueLearnersCount, 0)
    : 0;
  const selectedScormExport = data?.scorm?.exports.find((item) => item.id === selectedScormExportId);
  const activeScormExportsCount = data?.scorm
    ? data.scorm.exports.filter((item) => item.active).length
    : 0;

  const filteredStudentIds = filteredStudents.map((s) => s.student.id);
  const allFilteredSelected =
    filteredStudentIds.length > 0 &&
    filteredStudentIds.every((id) => selectedStudentIds.includes(id));

  const toggleStudentSelection = (studentId: string) => {
    setSelectedStudentIds((prev) =>
      prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
    );
  };

  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      setSelectedStudentIds((prev) => prev.filter((id) => !filteredStudentIds.includes(id)));
    } else {
      setSelectedStudentIds((prev) => Array.from(new Set([...prev, ...filteredStudentIds])));
    }
  };

  const handleSendMessage = () => {
    if (!courseId || selectedStudentIds.length === 0) return;
    const params = new URLSearchParams({ students: selectedStudentIds.join(',') });
    navigate(`/courses/${courseId}/student-messages?${params.toString()}`);
  };

  const handleDownloadCsv = () => {
    if (!data || selectedStudentIds.length === 0) return;
    const selected = data.students.filter((s) => selectedStudentIds.includes(s.student.id));
    if (selected.length === 0) return;
    const csv = buildStudentsCsv({
      students: selected,
      chapters: data.curriculum?.chapters || [],
      workshops: data.curriculum?.workshops || [],
      language: 'fr',
    });
    downloadCsvFile(buildStudentsCsvFilename(getAdminCourseName(data.course)), csv);
  };

  const handleOpenMessageHistory = (studentId: string) => {
    if (!courseId) return;
    navigate(`/courses/${courseId}/students/${studentId}/messages`);
  };

  useEffect(() => {
    if (courseId) {
      fetchCourseStudents();
    }
  }, [selectedScormExportId]);

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress />
        <Typography>Chargement des apprenants de la formation...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!data) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Typography>Aucune donnée disponible</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          {isTrackingOnly && (
            <Button sx={{ mb: 1 }} onClick={() => navigate('/tracking')}>
              ← Retour au suivi
            </Button>
          )}
          <Typography variant="h4" gutterBottom>
            {getAdminCourseName(data.course)} - Apprenants
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {canManage
              ? 'Gérer les apprenants inscrits à cette formation, par groupe.'
              : 'Consulter la progression des apprenants de cette formation.'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<BarChartIcon />}
            onClick={() =>
              navigate(
                selectedGroupId
                  ? `/courses/${courseId}/analytics?groupId=${selectedGroupId}`
                  : `/courses/${courseId}/analytics`
              )
            }
          >
            Statistiques
          </Button>
          {canManage && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setAddStudentOpen(true);
              fetchAvailableStudents();
            }}
          >
            Ajouter un apprenant
          </Button>
          )}
        </Stack>
      </Box>

      {/* Filtre par groupe */}
      {canManage && data.scorm && data.scorm.exports.length > 0 && (
        <Box mb={3} display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" color="text.secondary">
            Filtrer par export SCORM :
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Chip
              label="Tous les exports"
              color={selectedScormExportId === '' ? 'primary' : 'default'}
              variant={selectedScormExportId === '' ? 'filled' : 'outlined'}
              onClick={() => setSelectedScormExportId('')}
            />
            {data.scorm.exports.map((scormExport) => (
              <Chip
                key={scormExport.id}
                label={`${scormExport.clientId || scormExport.exportVersion} (${scormExport.uniqueLearnersCount})`}
                color={selectedScormExportId === scormExport.id ? 'primary' : 'default'}
                variant={selectedScormExportId === scormExport.id ? 'filled' : 'outlined'}
                onClick={() => setSelectedScormExportId(scormExport.id)}
              />
            ))}
          </Stack>
        </Box>
      )}

      {filterGroups.length > 0 && (
        <Box mb={3} display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" color="text.secondary">
            Filtrer par groupe :
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Chip
              label={`Tous (${data.summary.totalStudents})`}
              color={selectedGroupId === '' ? 'primary' : 'default'}
              variant={selectedGroupId === '' ? 'filled' : 'outlined'}
              onClick={() => setSelectedGroupId('')}
            />
            {filterGroups.map((g) => (
              <Chip
                key={g.id}
                label={`${g.name} (${g.studentCount})`}
                color={selectedGroupId === g.id ? 'primary' : 'default'}
                variant={selectedGroupId === g.id ? 'filled' : 'outlined'}
                onClick={() => setSelectedGroupId(g.id)}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Summary Cards */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <SchoolIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">
                    {selectedGroupId ? filteredStudents.length : data.summary.totalStudents}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total apprenants
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PersonIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">
                    {selectedGroupId ? filteredStudents.filter(s => s.enrollment.isActive).length : data.summary.activeStudents}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Apprenants actifs
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <CheckCircleIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">
                    {selectedGroupId
                      ? filteredStudents.filter(s => s.progress.progressPercentage === 100).length
                      : data.summary.completedStudents}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Terminés
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUpIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">
                    {selectedGroupId && filteredStudents.length > 0
                      ? `${Math.round(
                          filteredStudents.reduce((sum, s) => sum + s.progress.progressPercentage, 0) /
                            filteredStudents.length
                        )}%`
                      : `${data.summary.averageProgress}%`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Progression moyenne
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Stack>

      {canManage && data.scorm && data.scorm.exports.length > 0 && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  SCORM - Apprenants uniques (cumule)
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {scormTotalUniqueLearners}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  SCORM - Exports actifs
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {activeScormExportsCount}/{data.scorm.exports.length}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Export sélectionné
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {selectedScormExport ? selectedScormExport.uniqueLearnersCount : '-'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedScormExport
                    ? (selectedScormExport.clientId || selectedScormExport.exportVersion)
                    : 'Tous les exports'}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Stack>
      )}

      {/* Students Table */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        justifyContent="flex-end"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 1.5 }}
      >
        {canManage && (
          <Button
            variant="outlined"
            startIcon={<MailOutlineIcon />}
            onClick={handleSendMessage}
            disabled={selectedStudentIds.length === 0}
          >
            Envoyer un message ({selectedStudentIds.length})
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadCsv}
          disabled={selectedStudentIds.length === 0}
        >
          Télécharger CSV ({selectedStudentIds.length})
        </Button>
      </Stack>
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allFilteredSelected}
                    indeterminate={
                      selectedStudentIds.some((id) => filteredStudentIds.includes(id)) &&
                      !allFilteredSelected
                    }
                    onChange={toggleSelectAllFiltered}
                  />
                </TableCell>
                <TableCell>Apprenant</TableCell>
                <TableCell>Groupe</TableCell>
                <TableCell>Dernier message</TableCell>
                <TableCell>Progression</TableCell>
                <TableCell>Temps passé</TableCell>
                <TableCell>Dernière activité</TableCell>
                {data.course.diagnosticImpactEnabled && <TableCell>Impact</TableCell>}
                {canOpenStudentTracking && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredStudents.map((student) => (
                <TableRow
                  key={student.enrollmentId}
                  selected={selectedStudentIds.includes(student.student.id)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedStudentIds.includes(student.student.id)}
                      onChange={() => toggleStudentSelection(student.student.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        {student.student.firstName[0]}{student.student.lastName[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {student.student.firstName} {student.student.lastName}
                        </Typography>
                        {!isScormNoEmail(student.student.email) && (
                          <Typography variant="caption" color="text.secondary">
                            {student.student.email}
                          </Typography>
                        )}
                        {isScormNoEmail(student.student.email) && (
                          <Chip
                            label="SCORM sans email"
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ mt: 0.5, width: 'fit-content' }}
                          />
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={student.group?.name || 'Sans groupe'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {student.student.lastMessageAt ? (
                      canOpenStudentTracking ? (
                        <Link
                          component="button"
                          variant="body2"
                          underline="hover"
                          onClick={() => handleOpenMessageHistory(student.student.id)}
                          sx={{ textAlign: 'left' }}
                        >
                          {formatDate(student.student.lastMessageAt)}
                        </Link>
                      ) : (
                        <Typography variant="body2">
                          {formatDate(student.student.lastMessageAt)}
                        </Typography>
                      )
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Box display="flex" alignItems="center" mb={1}>
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          {student.progress.completedChapters}/{student.progress.totalChapters} sections
                        </Typography>
                        <Chip
                          label={`${student.progress.progressPercentage}%`}
                          size="small"
                          color={getProgressColor(student.progress.progressPercentage)}
                        />
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={student.progress.progressPercentage}
                        color={getProgressColor(student.progress.progressPercentage)}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={`${student.progress.totalTimeSpent} secondes`}>
                      <Typography variant="body2">
                        {formatTime(student.progress.totalTimeSpent)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {student.progress.lastActivity ? (
                      <Typography variant="body2">
                        {formatDate(student.progress.lastActivity)}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Aucune activité
                      </Typography>
                    )}
                  </TableCell>
                  {data.course.diagnosticImpactEnabled && (
                    <TableCell sx={{ verticalAlign: 'middle', py: 1 }}>
                      <Stack spacing={0.25}>
                        <Typography variant="body2" color="text.secondary" component="div">
                          Diagnostic :{' '}
                          <Box component="span" fontWeight={600} color="text.primary">
                            {student.assessment?.initialDone ? 'oui' : 'non'}
                          </Box>
                        </Typography>
                        <Typography variant="body2" color="text.secondary" component="div">
                          Impact :{' '}
                          <Box component="span" fontWeight={600} color="text.primary">
                            {student.assessment?.finalDone ? 'oui' : 'non'}
                          </Box>
                        </Typography>
                      </Stack>
                    </TableCell>
                  )}
                  {canOpenStudentTracking && (
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title="Historique des messages">
                          <IconButton
                            onClick={() => handleOpenMessageHistory(student.student.id)}
                            size="small"
                          >
                            <OutgoingMailIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Voir le détail">
                          <IconButton
                            onClick={() =>
                              navigate(
                                `/courses/${courseId}/students/${student.student.id}/detail`
                              )
                            }
                            size="small"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canManage && (
                          <>
                            <Tooltip title="Réinitialiser la progression">
                              <span>
                                <IconButton
                                  onClick={() => handleResetProgress(student.enrollmentId)}
                                  disabled={
                                    actionLoading === student.enrollmentId ||
                                    actionLoading === `reset:${student.enrollmentId}`
                                  }
                                  color="warning"
                                  size="small"
                                >
                                  <RestartAltIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Retirer de la formation">
                              <span>
                                <IconButton
                                  onClick={() => handleRemoveStudent(student.enrollmentId)}
                                  disabled={
                                    actionLoading === student.enrollmentId ||
                                    actionLoading === `reset:${student.enrollmentId}`
                                  }
                                  color="error"
                                  size="small"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Add Student Dialog */}
      <Dialog open={addStudentOpen} onClose={() => setAddStudentOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ajouter un apprenant à la formation</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={availableStudents}
            getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
            value={selectedStudent}
            onChange={(_, newValue) => setSelectedStudent(newValue)}
            onInputChange={(_, newInputValue) => {
              fetchAvailableStudents(newInputValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Rechercher et sélectionner un apprenant"
                placeholder="Saisir pour rechercher des apprenants..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            )}
            loading={actionLoading === 'search'}
            noOptionsText="Aucun apprenant trouvé"
            sx={{ mt: 2 }}
          />
          {groups.length > 0 && (
            <Box mt={3}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Assigner au groupe
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {groups.map((g) => (
                  <Chip
                    key={g.id}
                    label={g.name}
                    color={addStudentGroupId === g.id ? 'primary' : 'default'}
                    variant={addStudentGroupId === g.id ? 'filled' : 'outlined'}
                    onClick={() => setAddStudentGroupId(g.id)}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddStudentOpen(false)}>Annuler</Button>
          <Button
            onClick={handleAddStudent}
            variant="contained"
            disabled={!selectedStudent || actionLoading === 'add'}
          >
            {actionLoading === 'add' ? 'Ajout...' : 'Ajouter l’apprenant'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CourseStudents;
